import { getImportTypeLabel } from '../../app/terminology/imports';
import { Fragment, useId, useState } from 'react';
import type { FitmentImportHistoryItem } from '../../app/api/agent';
import { isPreparingTagImport, tagPreparationMessage } from './productTagImportUi';

const formatStageLabel = (stage?: string) => {
    if (!stage) {
        return null;
    }

    switch (stage.trim().toLowerCase()) {
        case 'processing':
            return 'Processing';
        case 'resolving_skus':
            return 'Resolving SKUs';
        case 'waiting_shopify_sync':
            return 'Waiting for Shopify sync';
        case 'blocked_shopify_sync':
            return 'Shopify sync needs attention';
        case 'processing_recovered_skus':
            return 'Processing recovered SKUs';
        case 'parsing':
            return 'Parsing';
        case 'applying':
            return 'Applying';
        case 'pricing':
            return 'Updating prices';
        case 'reading':
            return 'Reading tags';
        case 'reading_collection_mappings':
            return 'Reading collection mappings';
        case 'resolving_collections':
            return 'Resolving collections';
        case 'creating_vehicles':
            return 'Creating vehicle records';
        case 'reading_configurator_data':
            return 'Reading configurator data';
        case 'writing_configurator_data':
            return 'Writing variant attributes';
        case 'validating_configurator_products':
            return 'Validating products';
        case 'assigning_configurator_products':
            return 'Assigning configurator';
        case 'exporting':
            return 'Exporting';
        case 'tagging':
            return 'Tagging';
        case 'completed':
            return 'Completed';
        case 'completed_with_skipped_skus':
            return 'Completed with skipped SKUs';
        case 'completed_with_review':
            return 'Completed with review';
        case 'completed_with_tag_limit_review':
            return 'Tag limit — review required';
        case 'failed':
            return 'Failed';
        default:
            return stage;
    }
};

const statusLabels: Record<string, string> = {
    'shopify sync needs attention': 'Shopify sync needs attention',
    completed: 'Complete',
    complete: 'Complete',
    'completed with skipped skus': 'Completed with skips',
    'completed with review': 'Completed with review',
    'completed with tag limit review': 'Tag limit — review required',
    'pending review': 'Preview ready',
    discarded: 'Discarded',
    applied: 'Applied',
    undone: 'Undone',
    failed: 'Error',
    error: 'Error',
    queued: 'Queued',
    pending: 'Queued',
    'waiting for shopify sync': 'Waiting for Shopify sync',
    'resolving skus': 'Resolving SKUs',
    'processing recovered skus': 'Processing recovered SKUs',
    processing: 'In progress',
    in_progress: 'In progress',
    'in progress': 'In progress',
    exporting: 'Exporting',
    'tagging in progress': 'Tagging in progress',
    'tagging complete': 'Tagging complete',
    'tagging failed': 'Tagging failed',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
};

export const getStatusLabel = (state: string) => statusLabels[state.trim().toLowerCase()] ?? state;

export const getMappingRulesSummary = (item: FitmentImportHistoryItem) => {
    if (item.importFormat === 'atd_price_file') {
        return 'Updates variant prices from MAP and inventory item costs';
    }

    if (item.importFormat === 'product_tags') {
        return item.state.toLowerCase() === 'discarded' ? 'Preview discarded. No fitments were changed.'
            : 'Finds vehicle matches in Shopify product tags for you to review before importing.';
    }

    if (item.importFormat === 'fitment_export') {
        return 'Exports active product and variant fitment assignments';
    }

    if (item.importFormat === 'csv_xlsx') {
        return item.csvXlsxImportMode === 'replace_matched_skus'
            ? 'Replaced existing fitments for matched SKUs'
            : 'Appended fitments to existing mappings';
    }

    if (item.importFormat === 'collection_mappings_csv_xlsx') {
        return 'Upserted vehicle-to-collection mappings; product mappings were unchanged';
    }

    if (item.importFormat === 'configurator_data_csv_xlsx') {
        return item.configuratorAssignmentMode === 'update_only'
            ? 'Updated Shopify variant metafields; product assignments were unchanged'
            : 'Updated Shopify variant metafields and validated bulk configurator assignments';
    }

    if (!item.restrictToVendor) {
        return 'Store-wide vendor mapping';
    }

    const vendorLabel = item.selectedVendorName || 'Unknown vendor';
    const normalizeLabel = item.normalizeSku ? 'SKU normalization on' : 'SKU normalization off';

    if (!item.useVendorSkuSuffixRule) {
        return `${vendorLabel} · SKU suffix rule off · ${normalizeLabel}`;
    }

    const suffixRule = item.vendorSkuSuffixRule?.trim() || '(empty)';
    const updateLabel = item.updateSavedVendorSkuSuffixRule ? 'saved as default' : 'import only';

    return `${vendorLabel} · Suffix "${suffixRule}" (${updateLabel}) · ${normalizeLabel}`;
};

const formatCount = (value?: number) => (typeof value === 'number' ? value.toLocaleString() : '0');

type ImportMetric = {
    label: string;
    value: string;
};

const hasRecordMetrics = (item: FitmentImportHistoryItem) => [
    item.parsedRecordCount, item.appliedRecordCount, item.declaredRecordCount,
    item.csvXlsxImportedVehicleRecordCount, item.csvXlsxAppliedAssignmentCount,
    item.csvXlsxUniversalProductsMarkedCount, item.csvXlsxUniversalProductsClearedCount,
    item.collectionMappingCreatedVehicleRecordCount, item.collectionMappingAddedCount,
    item.collectionMappingReassignedCount, item.collectionMappingUnchangedCount,
    item.collectionMappingSkippedRowCount, item.configuratorMatchedVariantCount,
    item.configuratorUpdatedMetafieldCount, item.configuratorUpdatedVariantCount,
    item.configuratorAssignedProductCount, item.configuratorSkippedProductCount, item.configuratorFailedWriteCount,
].some(value => typeof value === 'number');

const getRecordMetrics = (item: FitmentImportHistoryItem): ImportMetric[] => {
    if (item.importFormat === 'fitment_export') {
        return [{
            label: 'Assignments exported',
            value: formatCount(item.appliedRecordCount ?? item.parsedRecordCount),
        }];
    }

    if (item.importFormat === 'csv_xlsx') {
        const metrics: ImportMetric[] = [
            { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
        ];

        if (typeof item.csvXlsxImportedVehicleRecordCount === 'number') {
            metrics.push({
                label: 'Vehicle records',
                value: formatCount(item.csvXlsxImportedVehicleRecordCount),
            });
        }

        metrics.push({
            label: 'Product assignments',
            value: formatCount(item.csvXlsxAppliedAssignmentCount ?? item.appliedRecordCount),
        });
        if ((item.csvXlsxUniversalProductsMarkedCount ?? 0) > 0)
            metrics.push({ label: 'Products marked universal', value: formatCount(item.csvXlsxUniversalProductsMarkedCount) });
        if ((item.csvXlsxUniversalProductsClearedCount ?? 0) > 0)
            metrics.push({ label: 'Universal status cleared', value: formatCount(item.csvXlsxUniversalProductsClearedCount) });

        return metrics;
    }

    if (item.importFormat === 'collection_mappings_csv_xlsx') {
        return [
            { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
            { label: 'Vehicles created', value: formatCount(item.collectionMappingCreatedVehicleRecordCount) },
            { label: 'Mappings added', value: formatCount(item.collectionMappingAddedCount) },
            { label: 'Reassigned', value: formatCount(item.collectionMappingReassignedCount) },
            { label: 'Already mapped', value: formatCount(item.collectionMappingUnchangedCount) },
            { label: 'Review rows', value: formatCount(item.collectionMappingSkippedRowCount) },
        ];
    }

    if (item.importFormat === 'configurator_data_csv_xlsx') {
        return [
            { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
            { label: 'Variants matched', value: formatCount(item.configuratorMatchedVariantCount) },
            { label: 'Metafields updated', value: formatCount(item.configuratorUpdatedMetafieldCount) },
            { label: 'Products assigned', value: formatCount(item.configuratorAssignedProductCount) },
            { label: 'Products skipped', value: formatCount(item.configuratorSkippedProductCount) },
            { label: 'Failed writes', value: formatCount(item.configuratorFailedWriteCount) },
        ];
    }

    return [
        { label: 'Rows declared', value: formatCount(item.declaredRecordCount) },
        { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
        { label: 'Rows applied', value: formatCount(item.appliedRecordCount) },
    ];
};

const formatImportDate = (value: string) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
};

type ImportDateGroup = {
    key: string;
    label: string;
};

const getLocalDateKey = (date: Date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
].join('-');

const getImportDateGroup = (value: string): ImportDateGroup => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return {
            key: `unknown-${value}`,
            label: 'Date unavailable',
        };
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const key = getLocalDateKey(date);

    if (key === getLocalDateKey(today)) {
        return { key, label: 'Today' };
    }

    if (key === getLocalDateKey(yesterday)) {
        return { key, label: 'Yesterday' };
    }

    return {
        key,
        label: new Intl.DateTimeFormat(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        }).format(date),
    };
};

const getHistoryItemTone = (state: string) => {
    const normalizedState = state.trim().toLowerCase();

    if (normalizedState.includes('failed') || normalizedState.includes('error') || normalizedState === 'shopify sync needs attention') {
        return 'critical';
    }

    if (normalizedState.includes('skipped') || normalizedState.includes('review')) {
        return 'warning';
    }

    if (
        normalizedState.includes('processing')
        || normalizedState.includes('queued')
        || normalizedState.includes('waiting')
        || normalizedState.includes('resolving')
        || normalizedState.includes('exporting')
        || normalizedState.includes('tagging in progress')
    ) {
        return 'info';
    }

    if (
        normalizedState.includes('complete')
        || normalizedState === 'applied'
        || normalizedState === 'tagging complete'
    ) {
        return 'success';
    }

    return 'default';
};

type StageProgress = {
    label: string;
    value: number;
};

const getStageProgress = (item: FitmentImportHistoryItem): StageProgress | null => {
    const stage = item.processingStage?.trim().toLowerCase();
    const declared = item.declaredRecordCount;
    const parsed = item.parsedRecordCount;
    const applied = item.appliedRecordCount;

    if (stage === 'parsing' && typeof declared === 'number' && declared > 0 && typeof parsed === 'number') {
        const value = Math.round((Math.min(parsed, declared) / declared) * 100);
        return {
            label: `Parsing rows ${formatCount(parsed)} / ${formatCount(declared)}`,
            value,
        };
    }

    if (stage === 'applying' && typeof parsed === 'number' && parsed > 0 && typeof applied === 'number') {
        const value = Math.round((Math.min(applied, parsed) / parsed) * 100);
        return {
            label: `Applying rows ${formatCount(applied)} / ${formatCount(parsed)}`,
            value,
        };
    }

    if (stage === 'pricing' && typeof parsed === 'number' && parsed > 0 && typeof applied === 'number') {
        const value = Math.round((Math.min(applied, parsed) / parsed) * 100);
        return {
            label: `Updating matched variants ${formatCount(applied)} / ${formatCount(parsed)}`,
            value,
        };
    }

    if (stage === 'exporting') {
        return {
            label: 'Writing fitment export CSV',
            value: typeof item.progressPercent === 'number'
                ? Math.max(0, Math.min(100, item.progressPercent))
                : 0,
        };
    }

    if (stage === 'tagging') {
        if (typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0) {
            const processed = Math.max(0, Math.min(item.taggingProcessedCount ?? 0, item.taggingTotalCount));
            const value = Math.round((processed / item.taggingTotalCount) * 100);
            const actionLabel = item.taggingPhase?.trim().toLowerCase() === 'fetching'
                ? 'Fetching product tags'
                : 'Applying product tags';

            return {
                label: `${actionLabel} ${formatCount(processed)} / ${formatCount(item.taggingTotalCount)}`,
                value,
            };
        }

        return {
            label: 'Tagging changed products',
            value: typeof item.progressPercent === 'number'
                ? Math.max(0, Math.min(100, item.progressPercent))
                : 0,
        };
    }

    return null;
};

const getStageDiagnostics = (item: FitmentImportHistoryItem) => {
    switch (item.processingStage?.trim().toLowerCase()) {
        case 'parsing':
            return 'Parsing the input file and normalizing source rows.';
        case 'applying':
            return 'Writing parsed rows to fitment records and deduplicating.';
        case 'resolving_skus':
            return 'Looking up missing SKUs directly in Shopify.';
        case 'waiting_shopify_sync':
            return 'Waiting for Shopify product and variant sync to finish.';
        case 'processing_recovered_skus':
            return 'Applying fitment rows for SKUs recovered from Shopify.';
        case 'pricing':
            return 'Matching ATD part numbers to Shopify variant SKUs and updating prices.';
        case 'exporting':
            return 'Writing assigned product fitments to a downloadable CSV.';
        case 'reading':
            return 'Reading synced Shopify product tags.';
        case 'reading_collection_mappings':
            return 'Reading and normalizing the collection mapping spreadsheet.';
        case 'resolving_collections':
            return 'Matching spreadsheet destinations to the shop\'s synced Shopify collections.';
        case 'creating_vehicles':
            return 'Creating any canonical vehicle records that do not exist yet.';
        case 'reading_configurator_data':
            return 'Reading variant identifiers and the selected configurator’s dynamic attribute columns.';
        case 'writing_configurator_data':
            return 'Writing variant-level metafields to Shopify in safe batches.';
        case 'validating_configurator_products':
            return 'Checking required attributes, fitments, and unique variant resolution before assignment.';
        case 'assigning_configurator_products':
            return 'Applying the selected configurator to products that passed validation.';
        case 'tagging':
            return 'Refreshing product tags for changed fitments.';
        default:
            return null;
    }
};

type Props = {
    items: FitmentImportHistoryItem[];
    archivingIds: Record<number, boolean>;
    onArchive: (queueId: number) => void;
    onDownload: (item: FitmentImportHistoryItem) => void;
    onDownloadReview?: (item: FitmentImportHistoryItem) => void;
    downloadingIds: Record<number, boolean>;
    downloadingReviewIds?: Record<number, boolean>;
    reviewingProductTagIds?: Record<number, boolean>;
    undoingProductTagIds?: Record<number, boolean>;
    onReviewProductTags?: (item: FitmentImportHistoryItem) => void;
    onDiscardProductTags?: (item: FitmentImportHistoryItem) => void;
    onUndoProductTags?: (item: FitmentImportHistoryItem) => void;
};

type GroupedHistoryItems = ImportDateGroup & {
    items: FitmentImportHistoryItem[];
};

const groupHistoryItems = (items: FitmentImportHistoryItem[]) => (
    items.reduce<GroupedHistoryItems[]>((groups, item) => {
        const dateGroup = getImportDateGroup(item.addedOn);
        const currentGroup = groups[groups.length - 1];

        if (currentGroup?.key === dateGroup.key) {
            currentGroup.items.push(item);
            return groups;
        }

        groups.push({
            ...dateGroup,
            items: [item],
        });
        return groups;
    }, [])
);

const getHistoryTimingLabel = (item: FitmentImportHistoryItem, stageLabel: string | null) => {
    const normalizedState = item.state.trim().toLowerCase();
    const isTerminalState = normalizedState.includes('complete')
        || normalizedState.includes('failed')
        || normalizedState.includes('error')
        || normalizedState === 'applied'
        || normalizedState === 'undone'
        || normalizedState === 'discarded'
        || normalizedState === 'cancelled'
        || normalizedState === 'canceled';

    if (item.completedAt) {
        return `Finished ${formatImportDate(item.completedAt)}`;
    }

    if (item.startedAt) {
        return stageLabel && !isTerminalState
            ? `${stageLabel} · Started ${formatImportDate(item.startedAt)}`
            : `Started ${formatImportDate(item.startedAt)}`;
    }

    return isTerminalState ? null : stageLabel;
};

const getResultSummary = (item: FitmentImportHistoryItem) => {
    const countLabel = (count: number | undefined, singular: string, plural: string) =>
        typeof count === 'number' ? count.toLocaleString() + ' ' + (count === 1 ? singular : plural) : null;
    if (item.importFormat === 'product_tags' && item.awaitingProductTagApproval) {
        return countLabel(item.productTagWillApplyCount, 'match ready to import', 'matches ready to import');
    }
    if (item.state.toLowerCase() === 'discarded') return 'Preview discarded. No fitments were changed.';
    if (getHistoryItemTone(item.state) === 'info' || getHistoryItemTone(item.state) === 'critical'
        || ['undone', 'cancelled', 'canceled', 'pending', 'in_progress', 'in progress'].includes(item.state.trim().toLowerCase())) return null;
    switch (item.importFormat) {
        case 'csv_xlsx':
            return [countLabel(item.csvXlsxAppliedAssignmentCount ?? item.appliedRecordCount, 'product assignment', 'product assignments'),
                (item.csvXlsxUniversalProductsMarkedCount ?? 0) > 0 ? countLabel(item.csvXlsxUniversalProductsMarkedCount, 'product marked universal', 'products marked universal') : null,
                (item.csvXlsxUniversalProductsClearedCount ?? 0) > 0 ? countLabel(item.csvXlsxUniversalProductsClearedCount, 'universal status cleared', 'universal statuses cleared') : null,
            ].filter(Boolean).join(' · ') || null;
        case 'fitment_export':
            return countLabel(item.appliedRecordCount ?? item.parsedRecordCount, 'assignment exported', 'assignments exported');
        case 'collection_mappings_csv_xlsx':
            return countLabel(item.collectionMappingAddedCount, 'mapping added', 'mappings added');
        case 'configurator_data_csv_xlsx':
            return countLabel(item.configuratorUpdatedVariantCount, 'variant updated', 'variants updated');
        default:
            return countLabel(item.appliedRecordCount, 'row applied', 'rows applied');
    }
};

const ImportHistoryRow = ({ item, archivingIds, onArchive, onDownload, onDownloadReview, downloadingIds,
    downloadingReviewIds = {}, reviewingProductTagIds = {}, undoingProductTagIds = {},
    onReviewProductTags, onDiscardProductTags, onUndoProductTags,
}: Omit<Props, 'items'> & { item: FitmentImportHistoryItem }) => {
    const [expanded, setExpanded] = useState(false);
    const detailsId = useId();
    const menuId = useId();
    const syncBlocked = item.processingStage === 'blocked_shopify_sync';
    const syncWaiting = syncBlocked || item.processingStage === 'waiting_shopify_sync';
    const progressValue = !syncWaiting && typeof item.progressPercent === 'number' ? Math.max(0, Math.min(100, item.progressPercent)) : undefined;
    const preparingTags = isPreparingTagImport(item);
    const stageLabel = preparingTags ? tagPreparationMessage(item) : formatStageLabel(item.processingStage);
    const timingLabel = getHistoryTimingLabel(item, stageLabel);
    const tone = getHistoryItemTone(item.state);
    const normalizedState = item.state.trim().toLowerCase();
    const canDownloadExport = item.importFormat === 'fitment_export' && !!item.storedFileName
        && ['completed', 'complete'].includes(normalizedState);
    const isActive = tone === 'info' || ['pending', 'in_progress', 'in progress'].includes(normalizedState);
    const stageProgress = preparingTags ? null : getStageProgress(item);
    const stageDiagnostics = item.importFormat === 'product_tags' ? null : getStageDiagnostics(item);
    const recordMetrics = getRecordMetrics(item);
    const resultSummary = getResultSummary(item);
    const canReviewFile = ['csv_xlsx', 'collection_mappings_csv_xlsx', 'configurator_data_csv_xlsx'].includes(item.importFormat)
        && !!item.storedFileName && !!onDownloadReview;
    const needsFileReview = canReviewFile && (normalizedState.includes('skipped') || normalizedState.includes('review'));
    const hasTagReview = item.importFormat === 'product_tags' && !!onReviewProductTags
        && (item.awaitingProductTagApproval || ['completed', 'applied', 'undone'].includes(normalizedState));
    const addedOn = new Date(item.addedOn);
    const timeLabel = Number.isNaN(addedOn.getTime()) ? 'Date unavailable'
        : addedOn.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    const importMode = item.importFormat === 'csv_xlsx' ? item.csvXlsxImportMode === 'replace_matched_skus' ? 'Replace matched SKUs' : 'Append' : null;
    const fileName = item.originalFileName || 'Unnamed file';
    const status = item.awaitingProductTagApproval ? 'Preview ready'
        : preparingTags ? syncBlocked ? 'Needs attention' : 'Preparing preview'
        : getStatusLabel(item.state);
    const downloading = !!downloadingIds[item.queueId];
    const reviewingFile = !!downloadingReviewIds[item.queueId];
    const reviewingTags = !!reviewingProductTagIds[item.queueId];
    const archiving = !!archivingIds[item.queueId];

    return (
        <article aria-label={fileName}>
            <s-box padding="base">
                <s-stack gap="small-200">
                    <s-grid gridTemplateColumns="@container (inline-size > 560px) 1fr auto, 1fr" gap="small-200" alignItems="start">
                        <s-stack gap="small-100">
                            <s-text fontWeight="semibold">{fileName}</s-text>
                            <s-text color="subdued">
                                {[getImportTypeLabel(item.importFormat), importMode, timeLabel].filter(Boolean).join(' · ')}
                            </s-text>
                            {resultSummary && <s-text>{resultSummary}</s-text>}
                            {needsFileReview && (
                                <s-stack direction="inline">
                                    <s-button variant="tertiary" onClick={() => onDownloadReview?.(item)} loading={reviewingFile} disabled={reviewingFile}>
                                        {normalizedState.includes('skipped') ? 'Review skipped SKUs' : 'Review import issues'}
                                    </s-button>
                                </s-stack>
                            )}
                            {hasTagReview && item.awaitingProductTagApproval && (
                                <s-stack direction="inline" gap="small-200" alignItems="center">
                                    <s-button variant="primary" onClick={() => onReviewProductTags?.(item)} loading={reviewingTags} disabled={reviewingTags}>
                                        Review preview
                                    </s-button>
                                    {onDiscardProductTags && (
                                        <s-button variant="tertiary" tone="critical" onClick={() => onDiscardProductTags(item)}>Discard preview</s-button>
                                    )}
                                </s-stack>
                            )}
                        </s-stack>
                        <s-stack direction="inline" gap="small-200" alignItems="center">
                            <s-text>{status}</s-text>
                            {item.isArchived && <s-text color="subdued">Archived</s-text>}
                            {canDownloadExport && (
                                <s-button icon="download" onClick={() => onDownload(item)} loading={downloading} disabled={downloading}>Download</s-button>
                            )}
                            <s-button variant="tertiary" icon={expanded ? 'chevron-up' : 'chevron-down'}
                                aria-expanded={String(expanded)} aria-controls={detailsId}
                                onClick={() => setExpanded(current => !current)}>
                                {expanded ? 'Hide details' : 'Show details'}
                            </s-button>
                            {!item.isArchived && <>
                                <s-button variant="tertiary" icon="menu-horizontal" commandFor={menuId}
                                    accessibilityLabel={'More actions for ' + (item.originalFileName || 'import')}
                                    loading={archiving} disabled={archiving} />
                                <s-menu id={menuId} accessibilityLabel={'Actions for ' + fileName}>
                                    <s-button icon="archive" disabled={archiving} onClick={() => onArchive(item.queueId)}>
                                        {item.importFormat === 'fitment_export' ? 'Archive export' : 'Archive import'}
                                    </s-button>
                                </s-menu>
                            </>}
                        </s-stack>
                    </s-grid>

                    {(isActive || syncBlocked) && (
                        <div role="status">
                            <s-stack gap="small-200">
                                {syncBlocked && (
                                    <s-stack direction="inline">
                                        <s-button href="/storefront?section=data-refresh">Open Shopify sync settings</s-button>
                                    </s-stack>
                                )}
                                <s-stack gap="small-100">
                                    <s-grid gridTemplateColumns="1fr auto" gap="small-200">
                                        <s-text>{preparingTags ? tagPreparationMessage(item) : item.processingMessage || stageLabel || 'Waiting to start'}</s-text>
                                        {progressValue !== undefined && <s-text color="subdued">{progressValue}%</s-text>}
                                    </s-grid>
                                    {progressValue !== undefined && <s-progress accessibilityLabel="Progress" value={progressValue} max={100} />}
                                </s-stack>
                                {stageProgress && (
                                    <s-stack gap="small-100">
                                        <s-text color="subdued">{stageProgress.label}</s-text>
                                        <s-progress accessibilityLabel={stageProgress.label} value={stageProgress.value} max={100} />
                                    </s-stack>
                                )}
                            </s-stack>
                        </div>
                    )}
                    {(item.error || tone === 'critical') && (
                        <s-text tone="critical">{item.error || 'This run failed. Show details for more information.'}</s-text>
                    )}

                    {expanded && (
                        <s-box id={detailsId} paddingBlockStart="small-200">
                            <s-stack gap="base">
                                <s-divider />
                                <s-text>{getMappingRulesSummary(item)}</s-text>
                                {item.importFormat !== 'product_tags' && hasRecordMetrics(item) && (
                                    <s-grid gridTemplateColumns="@container (inline-size > 560px) 1fr 1fr 1fr, 1fr" gap="base">
                                        {recordMetrics.map(metric => (
                                            <s-stack key={metric.label} gap="none">
                                                <s-text color="subdued">{metric.label}</s-text>
                                                <s-text fontWeight="semibold" fontVariantNumeric="tabular-nums">{metric.value}</s-text>
                                            </s-stack>
                                        ))}
                                    </s-grid>
                                )}
                                {isActive && stageDiagnostics && <s-text color="subdued">{stageDiagnostics}</s-text>}
                                {typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0 && (
                                    <s-text color="subdued">
                                        Tagging: Processed {formatCount(item.taggingProcessedCount)} / {formatCount(item.taggingTotalCount)}, Updated {formatCount(item.taggingUpdatedCount)}
                                    </s-text>
                                )}
                                <s-grid gridTemplateColumns="@container (inline-size > 560px) 1fr auto, 1fr" gap="small-200" alignItems="center">
                                    <s-stack gap="none">
                                        <s-text color="subdued">Queued {formatImportDate(item.addedOn)}</s-text>
                                        {timingLabel && <s-text color="subdued">{timingLabel}</s-text>}
                                        {item.isArchived && item.archivedAt && <s-text color="subdued">Archived {formatImportDate(item.archivedAt)}</s-text>}
                                    </s-stack>
                                    <s-stack direction="inline" gap="small-200" alignItems="center">
                                        {hasTagReview && (
                                            <s-button icon="view" onClick={() => onReviewProductTags?.(item)} loading={reviewingTags} disabled={reviewingTags}>
                                                {item.awaitingProductTagApproval ? 'Review' : 'Preview'}
                                            </s-button>
                                        )}
                                        {item.importFormat === 'product_tags' && item.productTagReviewStatus?.toLowerCase() === 'applied' && onUndoProductTags && (
                                            <s-button icon="undo" tone="critical" onClick={() => onUndoProductTags(item)}
                                                loading={!!undoingProductTagIds[item.queueId]}>Undo</s-button>
                                        )}
                                        {canReviewFile && (
                                            <s-button icon="view" onClick={() => onDownloadReview?.(item)} loading={reviewingFile} disabled={reviewingFile}>
                                                Review file
                                            </s-button>
                                        )}
                                        {item.storedFileName && item.importFormat !== 'fitment_export' && (
                                            <s-button icon="download" onClick={() => onDownload(item)} loading={downloading} disabled={downloading}>Download</s-button>
                                        )}
                                    </s-stack>
                                </s-grid>
                            </s-stack>
                        </s-box>
                    )}
                </s-stack>
            </s-box>
        </article>
    );
};

/** Runs grouped by day. Place it in a section with no padding so the rows reach the edges. */
const ImportHistoryCards = ({ items, ...props }: Props) => (
    <s-query-container>
        {groupHistoryItems(items).map((group, groupIndex) => (
            <section aria-label={group.label} key={group.key + '-' + groupIndex}>
                <s-box background="subdued" paddingInline="base" paddingBlock="small-200">
                    <s-grid gridTemplateColumns="1fr auto" gap="small-200">
                        <s-text color="subdued">{group.label}</s-text>
                        <s-text color="subdued">{group.items.length} {group.items.length === 1 ? 'run' : 'runs'}</s-text>
                    </s-grid>
                </s-box>
                {group.items.map((item, index) => (
                    <Fragment key={item.queueId}>
                        {index > 0 && <s-divider />}
                        <ImportHistoryRow item={item} {...props} />
                    </Fragment>
                ))}
            </section>
        ))}
    </s-query-container>
);

export default ImportHistoryCards;
